import { AddOutlined, Clear } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Stack,
  styled,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import updateHelper from 'immutability-helper';
import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import { SocialMediaPeople, ContactInformationPeople } from 'src/components/FormContributePeople';
import ModalComponent from 'src/components/Modal/ModalComponent';

// Utilities
import { useTranslation } from 'react-i18next';
import GuardContributeImg from 'src/asset/icons/guard-contribute.svg';
import { useNotification } from 'src/hooks/useNotification';
import { useMutateContributePeople } from 'src/services/mutate';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getCurrentUrl } from 'src/utils/helpers/stringHelpers';
import { validateURL } from 'src/utils/helpers/validate';
import {
  IPropsDataPeopleContribute,
  TypeContributePeople,
  TYPE_CONTRIBUTE_PEOPLE,
} from 'src/features/PeopleDetail/Common/config';
import { RELATIONSHIP_CONTRIBUTE_DATA } from 'src/features/Contributions/config';

interface IProps {
  peopleData: IPeopleDetail;
  open: boolean;
  type: TypeContributePeople | null;
  handleClose: () => void;
}

const StyledBoxData = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px dashed ${theme.palette.primary.main}`,
  position: 'relative',
  display: 'flex',
  padding: theme.spacing(2),
  flex: theme.spacing(1),
}));

const StyledIconDelete = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(-1),
  right: theme.spacing(-1),
  padding: theme.spacing(0.25),
  backgroundColor: theme.palette.action.active,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledHeaderAlert = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  padding: '8px 32px',
}));

const FormContributePeople: React.FC<IProps> = ({ peopleData, open, type, handleClose }) => {
  const theme = useTheme();
  const [countData, setCountData] = useState(1);
  const { mutateAsync } = useMutateContributePeople();
  const { notification } = useNotification();
  const { t } = useTranslation();

  const formik = useFormik<IPropsDataPeopleContribute>({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      type_of_contribution: type || TYPE_CONTRIBUTE_PEOPLE[0].value,
      relation_to_data: '',
      note: '',
      is_confirm: false,
      contribution_contact_people_profile: [],
      contribution_social_media_people_profile: [],
    },
    validationSchema: Yup.object().shape({
      type_of_contribution: Yup.string().max(255).required('Email is required'),
      relation_to_data: Yup.string().max(255).required('Relationship Data is required'),
      note: Yup.string().max(255),
      is_confirm: Yup.bool().oneOf([true], 'Please checked this checkbox'),
      contribution_social_media_people_profile: Yup.array().of(
        Yup.object().shape({
          website: Yup.string()
            .required('Website is required')
            .test('website_address', 'Does not match website syntax', (text?: string) => validateURL(text || '')),
        }),
      ),
      contribution_contact_people_profile: Yup.array().of(
        Yup.object().shape({
          address: Yup.string(),
          email: Yup.string(),
          phone_number: Yup.string(),
          phone_code: Yup.string(),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const params: TypeDTOContributePeople = {
        people_id: peopleData.id,
        name: peopleData?.full_name || '',
        type_of_contribution: values.type_of_contribution,
        relation_to_data: values.relation_to_data,
        note: values.note,
        ...(await formatParamsBeforeSent()),
      };

      const res: IResponse<ExpectedAny> = await mutateAsync(params);
      if (res.status_code === SUCCESS_CODE.CREATE) {
        handleClose();
      }
      setSubmitting(false);
      notification(
        res,
        {
          title: t('form_contribute.notification.success.title'),
          description: t('form_contribute.notification.success.description'),
        },
        {
          title: t('form_contribute.notification.failed.title'),
          description: t('form_contribute.notification.failed.description'),
        },
      );
    },
  });

  useEffect(() => {
    setCountData(1);
    formik.setValues({
      ...formik.initialValues,
      type_of_contribution: formik.values.type_of_contribution,
      relation_to_data: formik.values.relation_to_data,
      note: formik.values.note,
      is_confirm: formik.values.is_confirm,
    });
    formik.setErrors({});
    formik.setTouched({});
  }, [formik.values.type_of_contribution]);

  const removeData = (indexRemove: number) => {
    setCountData(countData - 1);
    switch (formik.values.type_of_contribution) {
      case TypeContributePeople.ContactPeopleProfile:
        removeValidateContribute('contribution_contact_people_profile', indexRemove);
        break;
      case TypeContributePeople.SocialPeopleCompanyProfile:
        removeValidateContribute('contribution_social_media_people_profile', indexRemove);
        break;
      default:
        break;
    }
  };

  const removeValidateContribute = (type: keyof IPropsDataPeopleContribute, indexRemove: number) => {
    formik.setFieldValue(type, updateHelper(formik.values[type], { $splice: [[indexRemove, 1]] }));
    if (isArray(formik.touched[type])) {
      formik.setTouched({
        ...formik.touched,
        [type]: updateHelper(formik.touched[type], {
          $splice: [[indexRemove, 1]],
        }),
      });
    }
    if (isArray(formik.errors[type])) {
      formik.setErrors({
        ...formik.errors,
        [type]: updateHelper(formik.errors[type], {
          $splice: [[indexRemove, 1]],
        }),
      });
    }
  };

  const formatParamsBeforeSent = async () => {
    const formattedValues: Omit<
      TypeDTOContributePeople,
      'people_id' | 'name' | 'type_of_contribution' | 'relation_to_data' | 'note'
    > = {
      contribution_contact_people_profile: null,
      contribution_social_media_people_profile: null,
    };

    switch (formik.values.type_of_contribution) {
      case TypeContributePeople.ContactPeopleProfile:
        formattedValues.contribution_contact_people_profile = formik.values.contribution_contact_people_profile;
        break;
      case TypeContributePeople.SocialPeopleCompanyProfile:
        formattedValues.contribution_social_media_people_profile =
          formik.values.contribution_social_media_people_profile;
        break;
      default:
        break;
    }
    return formattedValues;
  };

  return (
    <ModalComponent
      padding={4}
      pt={10}
      handleClose={handleClose}
      open={open}
      closeOnBackdropClick={false}
      disabledCloseIcon={formik.isSubmitting}
      showCloseIcon={true}
      headerAlert={
        <StyledHeaderAlert id="form-contribute-header-alert" direction="row" alignItems="center">
          <Box src={GuardContributeImg} component="img" width={24} mr={1} />
          <Typography variant="caption">Your personal information will remain</Typography>
          <Button variant="text" color="success">
            100% CONFIDENTIAL
          </Button>
        </StyledHeaderAlert>
      }
    >
      <FormikForm formik={formik}>
        <Box width={540}>
          <Typography textAlign={'center'} variant="h5">
            Contribute Data
          </Typography>
          <Typography textAlign={'center'} color={theme.palette.text.secondary} variant="body2">
            Please fill in the information in the form, we will confirm via your email.
          </Typography>

          <Stack direction={'column'} spacing={2.5} mt={4}>
            <TextField
              type="select"
              id="type_of_contribution"
              name="type_of_contribution"
              value={formik.values.type_of_contribution}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              select
              fullWidth
              label="Type Contribute"
              error={Boolean(formik.touched.type_of_contribution && formik.errors.type_of_contribution)}
              helperText={Boolean(formik.touched.type_of_contribution) && formik.errors.type_of_contribution}
            >
              {TYPE_CONTRIBUTE_PEOPLE.map((type) => (
                <MenuItem value={type.value} key={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControl>
              <Autocomplete
                clearIcon={false}
                disabled
                options={[]}
                freeSolo
                multiple
                renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                value={[`${getCurrentUrl()}`]}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled
                    size="small"
                    fullWidth
                    label={
                      <span>
                        Profile Contribute (URL){' '}
                        <TooltipFormLabel title="This is the Profile (URL) you will contribute to" />
                      </span>
                    }
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <Autocomplete
                clearIcon={false}
                disabled
                options={[]}
                freeSolo
                multiple
                renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                value={[peopleData?.full_name || '']}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                    disabled
                    size="small"
                    fullWidth
                    label={
                      <span>
                        People <TooltipFormLabel title="This is the people profile you are viewing" />
                      </span>
                    }
                  />
                )}
              />
            </FormControl>
            {Array(countData)
              .fill(null)
              .map((_, count) => {
                return (
                  <StyledBoxData key={count}>
                    {count > 0 && (
                      <StyledIconDelete size="small" onClick={() => removeData(count)}>
                        <Clear fontSize="small" />
                      </StyledIconDelete>
                    )}
                    {formik.values.type_of_contribution === TypeContributePeople.ContactPeopleProfile && (
                      <ContactInformationPeople indexData={count} />
                    )}
                    {formik.values.type_of_contribution === TypeContributePeople.SocialPeopleCompanyProfile && (
                      <SocialMediaPeople indexData={count} />
                    )}
                  </StyledBoxData>
                );
              })}
            <Box textAlign="center">
              <Button
                onClick={() => setCountData(countData + 1)}
                variant="outlined"
                color="secondary"
                startIcon={<AddOutlined />}
              >
                Add more
              </Button>
            </Box>
            <TextField
              type="select"
              id="relation_to_data"
              name="relation_to_data"
              value={formik.values.relation_to_data}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              select
              fullWidth
              required
              placeholder="Select option"
              label={
                <span>
                  Relationship to Data
                  <TooltipFormLabel title="Relationship to Data" />
                </span>
              }
              error={Boolean(formik.touched.relation_to_data && formik.errors.relation_to_data)}
              helperText={Boolean(formik.touched.relation_to_data) && formik.errors.relation_to_data}
            >
              {RELATIONSHIP_CONTRIBUTE_DATA.map((type) => (
                <MenuItem value={type.value} key={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="text"
              id="note"
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              fullWidth
              placeholder="Please describe detail hereSelect option"
              label={<span>Your Note To Change Data</span>}
              multiline
              rows={4}
              error={Boolean(formik.touched.note && formik.errors.note)}
              helperText={Boolean(formik.touched.note) && formik.errors.note}
            />
            <Box>
              <FormControlLabel
                id="is_confirm"
                name="is_confirm"
                onChange={(e, value) => {
                  formik.setFieldValue('is_confirm', value);
                }}
                value={formik.values.is_confirm}
                control={<Checkbox checked={formik.values.is_confirm} />}
                label={
                  <Typography color={theme.palette.text.secondary} variant="body1">
                    I certify that I am authorized to prove the above information
                  </Typography>
                }
              />
              {formik.touched.is_confirm && formik.errors.is_confirm && (
                <FormHelperText error>{formik.errors.is_confirm}</FormHelperText>
              )}
            </Box>
            <ButtonWithLoading
              fullWidth
              size="large"
              variant="contained"
              isRounded
              type="submit"
              isLoading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Submit
            </ButtonWithLoading>
          </Stack>
        </Box>
      </FormikForm>
    </ModalComponent>
  );
};
export default FormContributePeople;
